import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Marquee from "@/components/Marquee";
import PropertyCard from "@/components/PropertyCard";
import ContactRail from "@/components/ContactRail";
import CalculatorForm from "@/components/CalculatorForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORIA_IMG =
  "https://images.unsplash.com/photo-1603901622056-0a5bee231395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxhcmNoaXRlY3R1cmFsJTIwZHJhd2luZyUyMGJsdWVwcmludHMlMjB0b29sc3xlbnwwfHx8fDE3OTI5NzczODd8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [vendita, setVendita] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/properties?tipo=vendita`)
      .then((res) => setVendita(res.data.slice(0, 3)))
      .catch(() => setVendita([]));
  }, []);

  return (
    <>
      {/* HERO */}
      <header className="bg-[var(--ink)] text-white pt-16 pb-24 md:py-24 px-6 border-b border-[var(--ink)]/10 relative overflow-hidden">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-white/10 border border-white/15 font-mono-lbl text-xs text-[var(--gold-light)] uppercase tracking-wider mb-6">
            <span>Metodo di Valutazione Tecnica Ascari</span>
          </div>
          <h1 className="font-display text-[clamp(40px,5.4vw,84px)] font-black leading-[0.94] tracking-tight mb-8 max-w-4xl">
            <span className="asc-ln"><span>Valuta il tuo immobile.</span></span>
            <span className="asc-ln"><span className="text-[var(--gold)]">25 anni di cantiere e mercato.</span></span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl leading-relaxed mb-10">
            Dalla costruzione alla compravendita: valutiamo la tua casa con precisione tecnica per massimizzare il valore reale.
          </p>
          <div className="flex flex-wrap items-center gap-4 font-mono-lbl text-xs uppercase tracking-wider">
            <a
              href="#calcolatore"
              data-testid="hero-cta-calcolatore"
              className="px-6 py-3.5 bg-[var(--gold)] text-[var(--ink)] font-semibold rounded hover:bg-white transition-colors"
            >
              Apri Calcolatore Valore →
            </a>
            <a
              href="#contatto"
              data-testid="hero-cta-contatto"
              className="px-6 py-3.5 bg-white/10 border border-white/20 text-white rounded hover:bg-white/20 transition-colors"
            >
              Contatta l'Agenzia
            </a>
          </div>
        </div>
      </header>

      {/* CALCOLATORE IN PRIMO PIANO */}
      <section id="calcolatore" className="bg-[var(--paper)] px-6 pt-10 pb-24 md:pb-32 border-b border-[var(--ink)]/10 relative">
        <div className="max-w-6xl mx-auto -mt-24 md:-mt-32 relative z-20">
          <CalculatorForm />
        </div>
      </section>

      <Marquee />

      {/* STORIA TEASER */}
      <section className="bg-[var(--paper)] py-24 md:py-32 px-6 border-b border-[var(--ink)]/10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 space-y-6">
            <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-deep)] block">La Mia Storia</span>
            <h2 className="font-display text-[clamp(32px,3.8vw,52px)] font-bold leading-tight">
              25 Anni di solida esperienza.
            </h2>
            <div className="space-y-4 text-base text-[var(--ink)]/80 leading-relaxed">
              <p>
                Ho iniziato 25 anni fa nel settore della costruzione e ristrutturazione di immobili. Vivere il cantiere
                significa comprendere la qualità vera di muri, impianti e rifiniture.
              </p>
              <p>
                Dopo 10 anni ho aperto la mia agenzia immobiliare per offrire consulenze trasparenti e valorizzare ogni
                proprietà senza sovrastime illusorie o ribassi ingiustificati.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-2 font-mono-lbl">
              <div className="bg-white p-4 border border-[var(--ink)]/10 rounded">
                <span className="text-2xl font-bold text-[var(--gold-deep)] block">10 Anni</span>
                <span className="text-xs text-[var(--ink)]/70 uppercase">Cantiere Edile</span>
              </div>
              <div className="bg-white p-4 border border-[var(--ink)]/10 rounded">
                <span className="text-2xl font-bold text-[var(--ink)] block">15 Anni</span>
                <span className="text-xs text-[var(--ink)]/70 uppercase">Agenzia Dedicata</span>
              </div>
            </div>
            <Link
              to="/storia"
              data-testid="home-storia-link"
              className="inline-block font-mono-lbl text-xs uppercase tracking-wider text-[var(--gold-deep)] font-semibold hover:underline"
            >
              Leggi la storia completa →
            </Link>
          </div>
          <div className="lg:col-span-6">
            <figure className="relative aspect-[4/3] bg-[#eceae4] border border-[var(--ink)]/10 rounded-lg overflow-hidden">
              <img
                src={STORIA_IMG}
                alt="Documentazione tecnica e strumenti di misura su tavolo da disegno"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <figcaption className="absolute bottom-5 left-5 font-mono-lbl text-xs uppercase text-[var(--ink)] bg-white/90 px-3 py-1 rounded">
                Esperienza Tecnica Ascari
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* VENDITA PREVIEW */}
      <section className="bg-white py-24 md:py-32 px-6 border-b border-[var(--ink)]/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-deep)] block mb-2">In Evidenza</span>
              <h2 className="font-display text-[clamp(32px,3.8vw,52px)] font-bold leading-tight">Immobili in Vendita</h2>
            </div>
            <Link
              to="/vendita"
              data-testid="home-vendita-link"
              className="font-mono-lbl text-xs text-[var(--gold-deep)] uppercase font-semibold hover:underline"
            >
              Vedi tutte le vendite →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="home-vendita-grid">
            {vendita.map((p) => (
              <PropertyCard key={p.id} property={p} />
            ))}
          </div>
          <p className="mt-6 font-mono-lbl text-[10px] uppercase tracking-wider text-[var(--ink)]/40">
            Annunci reali pubblicati dall'agenzia · Modena e provincia
          </p>
        </div>
      </section>

      {/* SERVIZI IN AGENZIA */}
      <section className="bg-[var(--paper)] py-24 md:py-32 px-6 border-b border-[var(--ink)]/10" data-testid="servizi-section">
        <div className="max-w-6xl mx-auto">
          <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-deep)] block mb-2">
            Servizi in Agenzia
          </span>
          <h2 className="font-display text-[clamp(32px,3.8vw,52px)] font-bold leading-tight mb-12">
            Non solo compravendita.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-[var(--ink)]/10 rounded-lg p-8 flex flex-col justify-between">
              <div>
                <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-deep)] font-semibold block mb-3">
                  Broker Mutui in Sede
                </span>
                <h3 className="font-display font-bold text-xl mb-3">Mutui seguiti dal nostro broker</h3>
                <p className="text-sm text-[var(--ink)]/75 leading-relaxed">
                  In agenzia trovi un broker creditizio dedicato: confrontiamo le offerte di più banche e coordiniamo
                  pratica di mutuo e compravendita con un unico referente, fino al rogito.
                </p>
              </div>
              <Link
                to="/mutui"
                data-testid="servizi-cta-mutui"
                className="mt-6 inline-block font-mono-lbl text-xs uppercase tracking-wider text-[var(--gold-deep)] font-semibold hover:underline"
              >
                Calcola la rata e parla col broker →
              </Link>
            </div>
            <div className="bg-white border border-[var(--ink)]/10 rounded-lg p-8 flex flex-col justify-between">
              <div>
                <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-deep)] font-semibold block mb-3">
                  Geometra Abilitato
                </span>
                <h3 className="font-display font-bold text-xl mb-3">Pratiche tecniche senza intermediari</h3>
                <p className="text-sm text-[var(--ink)]/75 leading-relaxed">
                  Il nostro geometra abilitato segue direttamente pratiche catastali e urbanistiche, APE e ARE:
                  documenti sempre in regola prima della vendita, senza dover cercare un tecnico esterno.
                </p>
              </div>
              <Link
                to="/#contatto"
                data-testid="servizi-cta-geometra"
                className="mt-6 inline-block font-mono-lbl text-xs uppercase tracking-wider text-[var(--gold-deep)] font-semibold hover:underline"
              >
                Richiedi una pratica tecnica →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="bg-white py-20 px-6 border-b border-[var(--ink)]/10">
        <div className="max-w-6xl mx-auto">
          <div className="border border-[var(--ink)]/10 bg-[var(--paper)] rounded-xl p-8 md:p-12 text-center">
            <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-deep)] block mb-2">
              Trasparenza Ascari
            </span>
            <h3 className="font-display font-bold text-2xl mb-3">Esperienze dei Proprietari</h3>
            <p className="text-sm text-[var(--ink)]/70 max-w-lg mx-auto">
              Le testimonianze verificate dei nostri clienti saranno pubblicate in questa sezione al termine delle
              compravendite.
            </p>
          </div>
        </div>
      </section>

      <ContactRail />
    </>
  );
}
